import {
  DesktopMessage,
  DesktopMessageWrapperPatientProfile,
  useContact,
  useDesktop
} from '@revenuewell/front-end-bundle';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { ReactNode, useEffect, useState } from 'react';
import { useEventBus } from '../../../hooks/use-event-bus';
import { useNotification } from '../../../hooks/use-notification/use-notification';
import { EventJumpToMessageNativeNotification, EventSetUnreadBadge } from '../../../types/event-bus';
import {
  LocationSwitchEventData,
  SwitchLocationDialog
} from '../../shared/dialog/switch-location-dialog/switch-location-dialog';
interface NotificationEventListenerProps {
  children: ReactNode;
}

export const NotificationEventListener: React.FC<NotificationEventListenerProps> = ({ children }) => {
  const { listen, publish } = useEventBus();
  const { getContactsByPhoneNumber } = useContact();
  const { selectedLocation, locations, switchLocation } = useLocations();
  const { isDesktopApplication, listenMessage, setUnreadBadge } = useDesktop();
  const { showNotification } = useNotification();
  const [locationSwitchEvent, setLocationSwitchEvent] = useState<LocationSwitchEventData | null>(null);

  useEffect(() => {
    if (isDesktopApplication() && selectedLocation && locations) {
      // uc-notification project -> phone call -> patient profile click handler
      return listenMessage('desktop-wrapper-patient-profile', async (e: DesktopMessage) => {
        const payload = (e as DesktopMessageWrapperPatientProfile).payload;

        if (payload.locationId == selectedLocation.id) {
          goToPatientProfile(payload.phoneNumber, payload.locationId);
        } else {
          const contacts = await getContactsByPhoneNumber(payload.phoneNumber);

          setLocationSwitchEvent({
            locationId: selectedLocation.id,
            locationName: locations.find((l) => l.id == payload.locationId)?.name || '',
            patientPhone: payload.phoneNumber,
            patientName: contacts[0] ? `${contacts[0].firstName} ${contacts[0].lastName}` : undefined
          });
        }
      });
    }
  }, [selectedLocation, locations]);

  useEffect(() => {
    if (isDesktopApplication() && selectedLocation) {
      // messenger 1 project -> notification click handler
      const createJumpToMessageNativeNotification = (e: EventJumpToMessageNativeNotification) => {
        const onClick = async () => {
          publish({
            messageType: 'jumpToMessage',
            data: {
              conversationId: e.data.conversationId
            }
          });
        };
        showNotification(e.data.title, { body: e.data.body }, onClick);
      };

      return listen('jump-to-message-native-notification', (e) =>
        createJumpToMessageNativeNotification(e as EventJumpToMessageNativeNotification)
      );
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (isDesktopApplication()) {
      // messenger 1 project -> unread badge handler
      return listen('set-unread-badge', (e) => setUnreadBadge((e as EventSetUnreadBadge).data.unread));
    }
  }, []);

  const handleLocationSwitch = async (data: LocationSwitchEventData) => {
    setLocationSwitchEvent(null);
    await switchLocation(data.locationId);
    goToPatientProfile(data.patientPhone, data.locationId);
  };

  const goToPatientProfile = (phone: string, locId: number) => {
    publish({
      messageType: 'patientProfile',
      data: {
        phoneNumber: phone,
        locationId: locId
      }
    });
  };

  return (
    <>
      {children}
      {locationSwitchEvent && (
        <SwitchLocationDialog
          eventData={locationSwitchEvent}
          message="This call is from a patient in another location. Would you like to switch locations and go to their profile?"
          onSwitchLocation={handleLocationSwitch}
          onClose={() => setLocationSwitchEvent(null)}
        />
      )}
    </>
  );
};
